import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Calendar, Mail, Phone, MessageSquare, Trash2, Plus, Clock } from "lucide-react";

const SOURCE_LABELS = {
  contact: "Contact", general: "Contact", package: "Package",
  funeral_urgent: "Funeral urgent", funeral_quote: "Funeral quote",
  funeral_quote_sydney: "Funeral Sydney", funeral_quote_canberra: "Funeral Canberra",
  package_order: "Package order", voucher_order: "Voucher order",
  chatbot: "Chatbot", manual: "Manual",
};

function LeadModal({ lead, stages, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: lead.name || "", email: lead.email || "", phone: lead.phone || "",
    stage: lead.stage, due_date: lead.due_date || "",
  });
  const [noteText, setNoteText] = useState("");
  const [fullLead, setFullLead] = useState(lead);

  useEffect(() => {
    api.get(`/admin/leads/${lead.id}`).then((r) => setFullLead(r.data)).catch(() => {});
  }, [lead.id]);

  const save = async () => {
    try {
      const { data } = await api.put(`/admin/leads/${lead.id}`, form);
      setFullLead(data);
      toast.success("Saved");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/admin/leads/${lead.id}/notes`, { text: noteText.trim() });
      const { data } = await api.get(`/admin/leads/${lead.id}`);
      setFullLead(data);
      setNoteText("");
      toast.success("Note added");
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Deleted");
      onDeleted(lead.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const notes = (fullLead.notes || []).slice().reverse();
  const history = (fullLead.stage_history || []).slice().reverse();

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-start md:items-center justify-center overflow-y-auto p-4" data-testid="lead-modal">
      <div className="bg-white rounded-xl w-full max-w-3xl my-4 shadow-2xl">
        <div className="flex justify-between items-start p-6 border-b border-border-warm">
          <div>
            <p className="eyebrow mb-1">Lead · {SOURCE_LABELS[fullLead.source] || fullLead.source}</p>
            <h3 className="font-serif text-2xl">{fullLead.name || fullLead.email || "Untitled lead"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-cream rounded" data-testid="lead-modal-close"><X size={20} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Name</label>
            <input className="input-field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Email</label>
            <input className="input-field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Phone</label>
            <input className="input-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Stage</label>
            <select className="input-field" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="lead-stage">
              {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-warmgrey-muted mb-1 block">Follow-up date</label>
            <input type="date" className="input-field" value={form.due_date ? form.due_date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="lead-due" />
          </div>
        </div>
        {fullLead.metadata && Object.keys(fullLead.metadata).length > 0 && (
          <div className="px-6 pb-4">
            <p className="text-xs uppercase tracking-widest text-warmgrey-muted mb-2">Details</p>
            <div className="bg-cream rounded p-3 text-sm text-warmgrey space-y-1">
              {Object.entries(fullLead.metadata).map(([k, v]) => (
                <div key={k}><span className="font-medium text-headline">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</div>
              ))}
            </div>
          </div>
        )}
        <div className="px-6 pb-4">
          <p className="text-xs uppercase tracking-widest text-warmgrey-muted mb-2">Add a note</p>
          <textarea className="input-field min-h-[80px]" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Log a call, mention a decision…" data-testid="lead-note-input" />
          <button onClick={addNote} className="btn-outline mt-2 text-sm" data-testid="lead-add-note">Add note</button>
        </div>
        <div className="px-6 pb-4 grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-warmgrey-muted mb-2">Notes ({notes.length})</p>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {notes.length === 0 && <p className="text-sm text-warmgrey-muted">No notes yet.</p>}
              {notes.map((n) => (
                <div key={n.id} className={`p-3 rounded text-sm border ${n.system ? "border-primary/30 bg-primary/5" : "border-border-warm bg-cream"}`}>
                  <p className="whitespace-pre-wrap text-warmgrey">{n.text}</p>
                  <p className="text-xs text-warmgrey-muted mt-1">{new Date(n.created_at).toLocaleString()}{n.author ? ` · ${n.author}` : n.system ? " · system" : ""}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-warmgrey-muted mb-2">Stage history</p>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {history.map((h, i) => (
                <div key={i} className="text-sm text-warmgrey flex justify-between items-center py-1 border-b border-border-warm/70 last:border-0">
                  <span>{stages.find((s) => s.id === h.stage)?.label || h.stage}</span>
                  <span className="text-xs text-warmgrey-muted">{new Date(h.at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-border-warm flex justify-between">
          <button onClick={del} className="text-sm text-red-600 hover:underline flex items-center gap-1" data-testid="lead-delete"><Trash2 size={14} /> Delete</button>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-outline text-sm">Close</button>
            <button onClick={save} className="btn-primary text-sm" data-testid="lead-save">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadCard({ lead, onClick, onDragStart }) {
  const overdue = lead.due_date && new Date(lead.due_date) < new Date();
  return (
    <div
      draggable
      onDragStart={(e) => { onDragStart(lead.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={onClick}
      className="bg-white border border-border-warm rounded-lg p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
      data-testid={`lead-card-${lead.id}`}
    >
      <p className="font-medium text-headline text-sm mb-1 truncate">{lead.name || "(no name)"}</p>
      {lead.email && <p className="text-xs text-warmgrey-muted truncate flex items-center gap-1"><Mail size={11} /> {lead.email}</p>}
      {lead.phone && <p className="text-xs text-warmgrey-muted truncate flex items-center gap-1"><Phone size={11} /> {lead.phone}</p>}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border-warm/60">
        <span className="text-[10px] uppercase tracking-widest text-primary bg-primary/10 rounded px-1.5 py-0.5">
          {SOURCE_LABELS[lead.source] || lead.source}
        </span>
        <div className="flex items-center gap-2 text-xs text-warmgrey-muted">
          {lead.notes?.length > 0 && <span className="flex items-center gap-0.5"><MessageSquare size={11} /> {lead.notes.length}</span>}
          {lead.due_date && (
            <span className={`flex items-center gap-0.5 ${overdue ? "text-red-600" : ""}`}>
              <Clock size={11} /> {new Date(lead.due_date).toLocaleDateString("en-AU", { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCRM() {
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", email: "", phone: "", stage: "new-enquiry", notes_text: "" });

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/leads/stages"), api.get("/admin/leads")]);
      setStages(s.data);
      setLeads(l.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    stages.forEach((s) => (g[s.id] = []));
    leads.forEach((lead) => {
      if (g[lead.stage]) g[lead.stage].push(lead);
      else g["new-enquiry"]?.push(lead);
    });
    return g;
  }, [stages, leads]);

  const onDrop = async (stageId) => {
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    if (!lead || lead.stage === stageId) { setDragId(null); return; }
    // Optimistic
    setLeads((prev) => prev.map((l) => (l.id === dragId ? { ...l, stage: stageId } : l)));
    setDragId(null);
    try {
      const { data } = await api.put(`/admin/leads/${lead.id}`, { stage: stageId });
      setLeads((prev) => prev.map((l) => (l.id === data.id ? data : l)));
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const createLead = async () => {
    try {
      const { data } = await api.post("/admin/leads", { ...newLead, source: "manual" });
      setLeads((prev) => [data, ...prev]);
      setCreating(false);
      setNewLead({ name: "", email: "", phone: "", stage: "new-enquiry", notes_text: "" });
      toast.success("Lead created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="h-full flex flex-col" data-testid="admin-crm">
      <div className="flex justify-between items-center mb-4 shrink-0">
        <div>
          <h1 className="font-serif text-3xl">CRM Pipeline</h1>
          <p className="text-warmgrey text-sm mt-1">{leads.length} leads · drag cards between stages</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary text-sm" data-testid="crm-new-lead"><Plus size={16} /> Add Lead</button>
      </div>

      <div className="flex-1 overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max h-full">
          {stages.map((s) => (
            <div
              key={s.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s.id)}
              className="w-[280px] shrink-0 bg-cream rounded-lg flex flex-col max-h-[calc(100vh-14rem)]"
              data-testid={`stage-col-${s.id}`}
            >
              <div className="p-3 border-b border-border-warm sticky top-0 bg-cream rounded-t-lg">
                <div className="flex justify-between items-center">
                  <p className="text-xs uppercase tracking-widest text-secondary font-medium">{s.label}</p>
                  <span className="text-xs bg-white border border-border-warm rounded px-2 py-0.5 text-warmgrey">{(grouped[s.id] || []).length}</span>
                </div>
              </div>
              <div className="p-2 overflow-y-auto flex-1">
                {(grouped[s.id] || []).map((lead) => (
                  <LeadCard key={lead.id} lead={lead} onClick={() => setSelected(lead)} onDragStart={setDragId} />
                ))}
                {(grouped[s.id] || []).length === 0 && (
                  <p className="text-xs text-warmgrey-muted text-center py-8">Drop leads here</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <LeadModal
          lead={selected}
          stages={stages}
          onClose={() => setSelected(null)}
          onSaved={(updated) => setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)))}
          onDeleted={(id) => { setLeads((prev) => prev.filter((l) => l.id !== id)); setSelected(null); }}
        />
      )}

      {creating && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="crm-new-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-2xl">New Lead</h3>
              <button onClick={() => setCreating(false)} className="p-2 hover:bg-cream rounded"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <input className="input-field" placeholder="Name" value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} data-testid="crm-new-name" />
              <input className="input-field" placeholder="Email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} data-testid="crm-new-email" />
              <input className="input-field" placeholder="Phone" value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} data-testid="crm-new-phone" />
              <select className="input-field" value={newLead.stage} onChange={(e) => setNewLead({ ...newLead, stage: e.target.value })} data-testid="crm-new-stage">
                {stages.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <textarea className="input-field min-h-[80px]" placeholder="First note (optional)" value={newLead.notes_text} onChange={(e) => setNewLead({ ...newLead, notes_text: e.target.value })} />
              <button onClick={createLead} className="btn-primary w-full justify-center" data-testid="crm-new-save">Create Lead</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
